'use client'

import { Fragment } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { X, Plus, PackageX } from 'lucide-react'
import { formatImageURL, formatPrice, getBorderRadiusClass } from '@/utils/util'
import { Button } from '@/components/elements/Button'
import { useRewardVariantPicker, useVariantSelection } from '@/lib/commerce/hooks'
import VariantOptionPicker from '@/components/blocks/VariantOptionPicker'

function RewardPrice({ price, original }: { price: number, original?: number }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`text-sm sm:text-base font-medium ${price === 0 ? 'text-green-600' : 'text-red-600'}`}>{price === 0 ? 'Үнэгүй' : formatPrice(price)}</span>
      {!!original && price < original && <span className="line-through text-muted-foreground text-xs sm:text-sm">{formatPrice(original)}</span>}
    </div>
  )
}

function RewardOptionChoice({ product, vendor, getPrice, onAdd }: { product: any, vendor: any, getPrice: (_variant: any) => number, onAdd: (_variantId: string) => void }) {
  const { selectedVariant, selectedOptions, selectOption, isSelectionPurchasable, isVariantAvailable } = useVariantSelection(product)
  const canAdd = isSelectionPurchasable && !!selectedVariant?.id && isVariantAvailable(selectedVariant)

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <img src={formatImageURL(selectedVariant?.images?.[0] || product.image || 'placeholder.png')} alt={product.name} className="size-16 rounded-md object-cover" />
        <div className="min-w-0">
          <div className="text-sm text-muted-foreground truncate">{isSelectionPurchasable ? selectedVariant?.name : 'Энэ хослол байхгүй'}</div>
          {selectedVariant && <RewardPrice price={getPrice(selectedVariant)} original={selectedVariant.price} />}
        </div>
      </div>
      <VariantOptionPicker product={product} selected={selectedOptions} onSelect={selectOption} />
      <Button onClick={() => { if (selectedVariant?.id) onAdd(selectedVariant.id) }} disabled={!canAdd} width="full" size="lg" color={vendor?.color?.primary} icon={canAdd ? Plus : undefined}>{canAdd ? 'Сагсанд нэмэх' : 'Дууссан байна'}</Button>
    </div>
  )
}

export default function RewardVariantModal({ vendor }: { vendor: any }) {
  const { isOpen, close, reward, variants, optionProduct, add, getPrice, isAvailable } = useRewardVariantPicker()
  const borderRadius = vendor?.border ? getBorderRadiusClass(vendor.border) : 'rounded-md'

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-60" onClose={close}>
        <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/25" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 flex justify-center items-end sm:items-center sm:p-4">
          <TransitionChild as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95" enterTo="translate-y-0 sm:opacity-100 sm:scale-100" leave="transition ease-in-out duration-300 sm:duration-200 transform" leaveFrom="translate-y-0 sm:opacity-100 sm:scale-100" leaveTo="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95">
            <DialogPanel className="w-full sm:max-w-lg max-h-[85vh] sm:max-h-[calc(100vh-2rem)] flex flex-col transform overflow-hidden rounded-t-lg sm:rounded-lg bg-white text-left align-middle shadow-xl transition-all">
              <div className="bg-white border-b border-gray-200 px-4 py-4 sm:border-b-0 sm:px-6 sm:pt-6 sm:pb-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium leading-6 text-foreground">Бэлгийн барааны төрөл сонгох</h3>
                  <button type="button" aria-label="Хаах" className="rounded-md p-1 text-muted-foreground hover:text-foreground cursor-pointer" onClick={close}>
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-2 sm:mt-4">
                  <h4 className="font-medium text-foreground text-sm sm:text-base sm:mb-2">{reward?.name}</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">Та хүссэн төрлөө сонгоод сагсанд нэмнэ үү</p>
                </div>
              </div>

              <div className="p-4 sm:px-6 min-h-0 sm:max-h-104 overflow-y-auto">
                {optionProduct ? (
                  <RewardOptionChoice product={optionProduct} vendor={vendor} getPrice={getPrice} onAdd={add} />
                ) : (
                  <div className="space-y-3">
                    {variants.map((variant: any) => {
                      const available = isAvailable(variant)
                      return (
                        <div key={variant.id} className={`border ${borderRadius} p-3 transition-all ${available ? 'cursor-pointer hover:border-gray-300 hover:shadow-sm active:bg-gray-50' : 'opacity-50 cursor-not-allowed'}`} onClick={() => { if (available) add(variant.id) }}>
                          <div className="flex items-center gap-3">
                            <img src={formatImageURL(variant.images?.[0] || reward?.image || 'placeholder.png')} alt={variant.name} className={`w-14 h-14 sm:w-16 sm:h-16 object-cover shrink-0 ${borderRadius}`} />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-foreground text-sm sm:text-base truncate sm:whitespace-normal">{variant.name}</div>
                              <div className="mt-1">
                                <RewardPrice price={getPrice(variant)} original={variant.price} />
                              </div>
                              {!available && <div className="text-xs text-muted-foreground mt-1">Дууссан</div>}
                            </div>
                            <div className="shrink-0">
                              {available ? <Plus className="h-5 w-5 text-muted-foreground" /> : <PackageX className="h-5 w-5 text-muted-foreground" />}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="bg-white border-t border-gray-200 p-4 sm:border-t-0 sm:px-6 sm:pt-2 sm:pb-6">
                <Button onClick={close} className="w-full" variant="outline">Цуцлах</Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
